import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";

import useNotification from "../components/notifications/useNotifications";
import useJobAssemblySession from "../data/useJobAssemblySession";
import i18n from "../i18n";

export default function useJobAssemblyTimers(jobId?: string) {
  const { addNotification } = useNotification();
  const [nowMs, setNowMs] = useState<number>(Date.now());
  const { sessions: jobAssemblySessions, startSession, pauseSession, finishSession } = useJobAssemblySession(jobId);

  useEffect(() => {
    const interval = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const parseApiUtcDate = (value: string) => DateTime.fromISO(value, { zone: "utc" });

  const sessionRows = jobAssemblySessions.data?.data ?? [];

  const totalElapsedSeconds = useMemo(() => {
    // Assembly sessions are the source of truth for the total — job-level sessions
    // (null partAssemblyId) can overlap them and would double-count. Only when no
    // assembly session exists do we fall back to the job-level sessions, so the
    // yard app's whole-job timer still shows a total.
    const assemblyRows = sessionRows.filter((row) => !!row.partAssemblyId);
    const rowsToSum = assemblyRows.length > 0 ? assemblyRows : sessionRows;

    return rowsToSum.reduce((total, row) => {
      if (!row.dismantlingStart) return total;

      const start = parseApiUtcDate(row.dismantlingStart);
      const end = row.dismantlingEnd
        ? parseApiUtcDate(row.dismantlingEnd)
        : DateTime.utc();
      const diff = Math.floor(end.diff(start, "seconds").seconds);

      return total + (diff > 0 ? diff : 0);
    }, 0);
  }, [sessionRows, nowMs]);

  const firstStartedSession = useMemo(() => {
    return [...sessionRows]
      .filter((x) => !!x.dismantlingStart)
      .sort((a, b) => parseApiUtcDate(a.dismantlingStart!).toMillis() - parseApiUtcDate(b.dismantlingStart!).toMillis())[0];
  }, [sessionRows]);

  const lastSessionActivity = useMemo(() => {
    return [...sessionRows].sort((a, b) => {
      const aActivity = a.dismantlingEnd ?? a.dismantlingStart ?? a.created;
      const bActivity = b.dismantlingEnd ?? b.dismantlingStart ?? b.created;
      return parseApiUtcDate(bActivity).toMillis() - parseApiUtcDate(aActivity).toMillis();
    })[0];
  }, [sessionRows]);

  const hasActiveSession = useMemo(() => sessionRows.some((x) => !x.dismantlingEnd), [sessionRows]);
  const isAssemblyActionLoading = startSession.isLoading || pauseSession.isLoading || finishSession.isLoading;

  const handleAssemblyStart = (partAssemblyId: string) => {
    if (!jobId) return;

    startSession.mutate(
      { jobId, partAssemblyId },
      {
        onSuccess: () => {
          addNotification({
            variant: "success",
            primaryText: i18n.t("Assembly timer started", { ns: "jobs" }),
          });
        },
        onError: (error: any) => {
          addNotification({
            variant: "danger",
            primaryText: error?.message ?? i18n.t("Unable to start assembly timer", { ns: "jobs" }),
          });
        }
      }
    );
  };

  const handleAssemblyPause = (partAssemblyId: string, pauseReason: string, userId: string) => {
    if (!jobId) return;

    pauseSession.mutate(
      { jobId, partAssemblyId, pauseReason, userId },
      {
        onSuccess: () => {
          addNotification({
            variant: "success",
            primaryText: i18n.t("Assembly timer paused", { ns: "jobs" }),
          });
        },
        onError: (error: any) => {
          addNotification({
            variant: "danger",
            primaryText: error?.message ?? i18n.t("Unable to pause assembly timer", { ns: "jobs" }),
          });
        }
      }
    );
  };

  const handleAssemblyFinish = (partAssemblyId: string, userId: string) => {
    if (!jobId) return;

    finishSession.mutate(
      { jobId, partAssemblyId, userId },
      {
        onSuccess: () => {
          addNotification({
            variant: "success",
            primaryText: i18n.t("Assembly timer finished", { ns: "jobs" }),
          });
        },
        onError: (error: any) => {
          addNotification({
            variant: "danger",
            primaryText: error?.message ?? i18n.t("Unable to finish assembly timer", { ns: "jobs" }),
          });
        }
      }
    );
  };

  return {
    sessionRows,
    totalElapsedSeconds,
    firstStartedAt: firstStartedSession?.dismantlingStart ?? null,
    lastActivityAt: lastSessionActivity
      ? (lastSessionActivity.dismantlingEnd ?? lastSessionActivity.dismantlingStart ?? lastSessionActivity.created)
      : null,
    hasActiveSession,
    isAssemblyActionLoading,
    handleAssemblyStart,
    handleAssemblyPause,
    handleAssemblyFinish,
  };
}
